// Dialog — a controlled wrapper around the native <dialog> element.
//
// We keep <dialog> (free focus-trap, Esc-to-close, top-layer painting, inert
// background) but expose a declarative `open` prop instead of making every
// caller hold a ref and call showModal()/close() imperatively. The effect
// reconciles the DOM state to the prop; `onClose` fires for Esc/programmatic
// close so the parent can flip its state back.
import { ReactNode, useEffect, useRef } from 'react';
import { cn } from './cn';

export interface DialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  /** Width utility for the panel; override for wider dialogs. */
  readonly widthClass?: string;
  readonly className?: string;
  readonly children: ReactNode;
}

export function Dialog({
  open,
  onClose,
  widthClass = 'w-[min(90vw,28rem)]',
  className,
  children,
}: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    else if (!open && el.open) el.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      onClose={onClose}
      className="bg-transparent p-0 m-auto backdrop:bg-bg-base/60 backdrop:backdrop-blur-sm"
    >
      {/* Guard the body so its content only mounts while open — avoids stray
          autoFocus grabs and keeps form state fresh on each open. */}
      {open ? (
        <div className={cn('orb-card p-6 space-y-4', widthClass, className)}>{children}</div>
      ) : null}
    </dialog>
  );
}

export function DialogHeader({ eyebrow, title }: { readonly eyebrow?: string; readonly title: ReactNode }) {
  return (
    <header>
      {eyebrow ? <p className="orb-eyebrow mb-1">{eyebrow}</p> : null}
      <h2 className="font-display text-xl font-semibold text-fg">{title}</h2>
    </header>
  );
}

export function DialogFooter({
  children,
  className,
}: {
  readonly children: ReactNode;
  readonly className?: string;
}) {
  return <footer className={cn('flex justify-end gap-2 pt-2', className)}>{children}</footer>;
}
